import { inject, postConstruct } from 'inversify';
import { reactive, watch } from 'vue';

import { service } from '@/decorators/service';
import { Environment } from '@/services/Environment';
import { Logger } from '@/services/Logger';
import { IBetaFeature } from '~/IBetaFeature';
import { IBetaFeatures } from '~/IBetaFeatures';

const defaultBetaFeature: IBetaFeature = {
  id: '',
  enabled: false,
  label: '',
};

@service(BetaFeatures)
export class BetaFeatures implements Iterable<IBetaFeature> {
  // Services.
  @inject(Environment) environment: Environment;
  @inject(Logger) logger: Logger;

  // Properties.
  betaFeatures: IBetaFeatures = reactive({});

  @postConstruct()
  initialize() {
    try {
      const localBetaFeatures = <IBetaFeatures> JSON.parse(localStorage.getItem('betaFeatures') ?? '{}');
      this.betaFeatures = reactive({ ...localBetaFeatures });

      // Watch and save betaFeatures in localStorage to persist hard refresh; only if not in a production environment.
      watch(this.betaFeatures, () => {
        if (!this.environment.isProd) {
          localStorage.setItem('betaFeatures', JSON.stringify(this.betaFeatures));
        }
      }, { deep: true });
    }
    catch (e) {
      // eslint-disable-next-line no-console
      console.error(e as Error);
      this.logger.captureException(e as Error, { tags: { betaFeatures: 'initialize' } });
    }
  }

  * [Symbol.iterator](): IterableIterator<IBetaFeature> {
    const betaFeatures = Object.values(this.betaFeatures);
    for (let i = 0, l = betaFeatures.length; i < l; i += 1) {
      yield betaFeatures[i];
    }
  }

  // Getters.
  get allBetaFeaturesEnabled() {
    return Object.values(this.betaFeatures).every((f) => f.enabled);
  }

  get enabledBetaFeatures() {
    return Object.values(this.betaFeatures).filter((f) => f.enabled);
  }

  get length() {
    return Object.keys(this.betaFeatures).length;
  }

  // Methods.
  add(features: Record<string, Omit<IBetaFeature, 'enabled' | 'id'> | string>) {
    const registeredFeatures = Object.keys(features);

    registeredFeatures.forEach((id) => {
      const feature = features[id];
      if (typeof feature === 'string') {
        this.betaFeatures[id] = {
          // Merge default.
          ...defaultBetaFeature,
          // Merge existing value.
          ...this.betaFeatures[id],
          label: feature,
          id,
        };
      }
      else {
        this.betaFeatures[id] = {
          // Merge default.
          ...defaultBetaFeature,
          // Merge feature definition.
          ...feature,
          // Merge existing value.
          ...this.betaFeatures[id],
          id,
        };
      }
    });

    // Remove unknown (old) beta features.
    Object.keys(this.betaFeatures).forEach((id) => {
      if (registeredFeatures.indexOf(id) === -1) {
        delete this.betaFeatures[id];
      }
    });
  }

  all() {
    return Object.values(this.betaFeatures);
  }

  get(id?: string) {
    return id ? this.betaFeatures[id] : undefined;
  }

  has(id?: string) {
    return !!(id && this.betaFeatures[id] !== undefined);
  }

  toggle(betaFeature: string) {
    if (betaFeature !== undefined && this.betaFeatures[betaFeature] !== undefined) {
      this.betaFeatures[betaFeature].enabled = !this.betaFeatures[betaFeature].enabled;
    }
  }

  toggleAll() {
    const enabled = this.allBetaFeaturesEnabled;
    Object.values(this.betaFeatures).forEach((f) => {
      f.enabled = !enabled;
    });
  }
}
